/*
  Collection stock ordering: in stock → made to order → pre-order → out of stock.

  Shopify can't sort a collection by stock, and Liquid only sees one page (max 250 products), so this
  re-paginates the grid in two streams using the storefront Availability filter:
    1. every available product (filter.v.availability=1), in the collection's normal order,
    2. then every out-of-stock product (filter.v.availability=0).
  Page N shows items (N-1)*perPage … N*perPage of that combined list, fetched with the Section Rendering
  API. Within a page, available products are then ordered by tier (data-stock-tier from snippets/stock-tier.liquid)
  when the shopper hasn't picked their own sort.

  Config comes from <stock-order> rendered inside #ProductGridContainer (sections/main-collection-product-grid.liquid);
  facets.js swaps that container on every filter/sort/page change, which reconnects this element and re-runs it.
*/
(() => {
  if (customElements.get('stock-order')) return;

  const cache = new Map();

  const fetchPage = (params, availability, page, sectionId) => {
    const p = new URLSearchParams(params);
    p.delete('page');
    p.delete('section_id');
    p.delete('filter.v.availability');
    p.set('filter.v.availability', availability);
    p.set('page', page);
    p.set('section_id', sectionId);
    const url = `${window.location.pathname}?${p.toString()}`;
    if (!cache.has(url)) {
      cache.set(
        url,
        fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('#product-grid > li.grid-item[data-stock-tier]')];
          })
          .catch((err) => {
            cache.delete(url);
            throw err;
          }),
      );
    }
    return cache.get(url);
  };

  // Items [from, to) of one availability stream, fetched page by page at the section's page size.
  const fetchRange = async (params, availability, from, to, perPage, sectionId) => {
    if (to <= from) return [];
    const first = Math.floor(from / perPage) + 1;
    const last = Math.floor((to - 1) / perPage) + 1;
    const pages = [];
    for (let n = first; n <= last; n += 1) pages.push(fetchPage(params, availability, n, sectionId));
    const items = (await Promise.all(pages)).flat();
    const offset = (first - 1) * perPage;
    return items.slice(from - offset, to - offset);
  };

  const byTier = (items) =>
    items
      .map((el, i) => ({ el, i, t: Number(el.dataset.stockTier) || 4 }))
      .sort((a, b) => a.t - b.t || a.i - b.i)
      .map((x) => x.el);

  class StockOrder extends HTMLElement {
    connectedCallback() {
      this.run().finally(() => this.reveal());
      // Never leave the grid hidden if something stalls
      this.failsafe = setTimeout(() => this.reveal(), 5000);
    }

    disconnectedCallback() {
      clearTimeout(this.failsafe);
    }

    get grid() {
      return document.getElementById('product-grid');
    }

    reveal() {
      clearTimeout(this.failsafe);
      this.grid?.classList.remove('is-stock-pending');
    }

    replaceItems(items) {
      const grid = this.grid;
      if (!grid || !this.isConnected) return;
      grid.querySelectorAll(':scope > li.grid-item[data-stock-tier]').forEach((li) => li.remove());
      const tail = grid.querySelector(':scope > li.grid-item-block:last-child');
      items.forEach((li) => grid.insertBefore(document.importNode(li, true), tail));
    }

    async run() {
      const grid = this.grid;
      if (!grid) return;
      const d = this.dataset;
      const tierSort = d.tierSort === 'true';
      const current = [...grid.querySelectorAll(':scope > li.grid-item[data-stock-tier]')];

      // Availability filter already chosen, or only one stream exists: just order this page by tier.
      if (d.mode !== 'stream') {
        if (tierSort) this.replaceItems(byTier(current).map((el) => el.cloneNode(true)));
        return;
      }

      const perPage = Number(d.perPage);
      const page = Number(d.page) || 1;
      const available = Number(d.available);
      const start = (page - 1) * perPage;
      const end = start + perPage;
      const params = window.location.search;

      try {
        const [inStock, outOfStock] = await Promise.all([
          fetchRange(params, '1', start, Math.min(end, available), perPage, d.section),
          fetchRange(params, '0', Math.max(0, start - available), end - available, perPage, d.section),
        ]);
        if (!this.isConnected) return;
        this.replaceItems([...(tierSort ? byTier(inStock) : inStock), ...outOfStock]);
      } catch (err) {
        // Fall back to the page as Shopify rendered it
        console.warn('stock-order: falling back to default order', err);
      }
    }
  }

  customElements.define('stock-order', StockOrder);
})();
